import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clsx } from 'clsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import classes from './RevenueTable.module.scss';
import { GET_REVENUE_BY_YEAR_REQUEST } from '../../../../../redux/actions';
import { getFormattedCurrency } from '../../../../../utils/commonUtils';
import { isMobileModeSelector } from '../../../../../redux/selectors/appStatusSelector';
import { Typography } from '../../../../../shared/ui/typography';
import { revenueByYearSelector } from '../../../../../redux/selectors/revenueByYearSelector';
import { performancePagePeriodSelector } from '../../../../../redux/selectors/performancePageSelector';

import { listingsSelector } from '@/redux/selectors/listingsSelector';
import { IListing } from '@/views/interfaces';
import { useMemo } from 'react';

interface IRow {
  year: string;
  reservations: number;
  revenue: number;
}

export const RevenueTable = () => {
  const dispatch = useDispatch();
  const isMobileView = useSelector(isMobileModeSelector);
  const period = useSelector(performancePagePeriodSelector);
  const rows = useSelector(revenueByYearSelector(period))?.summary;
  const safeRows = Array.isArray(rows) ? rows : [];

  const listings: IListing[] = useSelector(listingsSelector);
  const body = useMemo(() => {
    return (listings !== null ? listings : [])
      .filter((listing) => listing.is_listed)
      .map((listing) => listing.property_id);
  }, [listings]);

  useEffect(() => {      
      dispatch({
      type: GET_REVENUE_BY_YEAR_REQUEST,
      payload: body,
    });
  }, [dispatch, body]);

  if (!safeRows || safeRows.length === 0) {
    return (
      <div
        className={clsx(classes.tableContainer, classes.errorMessageContainer)}
      >
        <Typography
          variant='text-lg'
          weight='medium'
          className={classes.noDataMessage}
        >
          No data available for the selected period.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.tableContainer}>
      <Typography
        className={clsx(classes.header, classes.gray900)}
        variant='text-xl'
        weight='semibold'
      >
        Revenue by Year
      </Typography>
      <div className={classes.tableWrapper}>
        <Table
          size='small'
          aria-label='Revenue table'
          className={classes.table}
        >
          <TableHead
            className={classes.tableHead}
            style={{ tableLayout: 'fixed' }}
          >
            <TableRow>
              <TableCell align='center'>
                <Typography
                  className={classes.tableHeaderCell}
                  variant={isMobileView ? 'text-xs' : 'text-md'}
                  weight='medium'
                >
                  YEAR
                </Typography>
              </TableCell>
              <TableCell align='center'>
                <Typography
                  className={classes.tableHeaderCell}
                  variant={isMobileView ? 'text-xs' : 'text-md'}
                  weight='medium'
                >
                  RESERVATIONS
                </Typography>
              </TableCell>
              <TableCell align='center'>
                <Typography
                  className={classes.tableHeaderCell}
                  variant={isMobileView ? 'text-xs' : 'text-md'}
                  weight='medium'
                >
                  REVENUE
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {safeRows &&
              safeRows.map(
                (
                  { year, reservations, revenue }: IRow,
                  index: number
                ) => {
                  // const newyear = new Date(year).getFullYear();
                  return (
                    <TableRow
                      className={classes.tableRow}
                      key={index}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#fff' : '#F7F8FC',
                      }}
                    >
                      <TableCell align='center'>
                        <Typography
                          className={classes.gray900}
                          variant={isMobileView ? 'text-md' : 'text-xl'}
                          weight='semibold'
                        >
                          {year}
                        </Typography>
                      </TableCell>
                      <TableCell align={isMobileView ? 'center' : 'center'}>
                        <Typography
                          className={classes.gray900}
                          variant={isMobileView ? 'text-md' : 'text-xl'}
                        >
                          {reservations}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <Typography
                          className={classes.gray900}
                          variant={isMobileView ? 'text-md' : 'text-xl'}
                        >
                          {getFormattedCurrency(revenue)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
